import React, { useState } from 'react';
import styled from 'styled-components';
import { Compass, ExternalLink, Lightbulb, ChevronDown, Layers } from 'lucide-react';
import colors from 'theme/colors';
import { panelAccent, panelAccentAlpha } from 'theme/panelAccent';
import { Card, Grid, Label, Muted, Section } from '../parts';
import { formatNumber } from '../labels';

export default function MarketTab({ analysis }) {
    const { niche } = analysis.result;

    return (
        <>
            <Section icon={Compass} title='Onde você está' subtitle={niche.verdict}>
                <Grid $min='280px'>
                    <Card>
                        <Label>Seu mercado</Label>
                        <Statement>{niche.macro}</Statement>
                    </Card>
                    <Card>
                        <Label>O que só você oferece</Label>
                        <Statement>{niche.positioning}</Statement>
                    </Card>
                </Grid>
            </Section>

            {niche.references.length > 0 && (
                <Section icon={ExternalLink} title='Perfis para se inspirar' subtitle='Cada um faz bem uma coisa que pode funcionar para você'>
                    <Card style={{ padding: 0 }}>
                        {niche.references.map(reference => (
                            <Reference key={reference.handle}>
                                <ReferenceHead>
                                    <ReferenceHandle
                                        href={`https://www.instagram.com/${reference.handle.replace('@', '')}/`}
                                        target='_blank'
                                        rel='noopener noreferrer'
                                    >
                                        {reference.handle}
                                    </ReferenceHandle>
                                    <ReferenceFollowers>{formatNumber(reference.followers)} seguidores</ReferenceFollowers>
                                </ReferenceHead>
                                <Muted>{reference.description}</Muted>
                                <Lesson><strong>O que levar para o seu perfil:</strong> {reference.lesson}</Lesson>
                            </Reference>
                        ))}
                    </Card>
                </Section>
            )}

            <Section icon={Layers} title='O que o mercado faz'>
                <Card style={{ padding: 0 }}>
                    <Comparison>
                        <ComparisonRow>
                            <ComparisonLabel>Todo mundo faz</ComparisonLabel>
                            <Muted>{niche.patterns.everyone_does}</Muted>
                        </ComparisonRow>
                        <ComparisonRow>
                            <ComparisonLabel>Ninguém faz</ComparisonLabel>
                            <Muted>{niche.patterns.open_space}</Muted>
                        </ComparisonRow>
                        <ComparisonRow $highlighted>
                            <ComparisonLabel>Sua vantagem</ComparisonLabel>
                            <Muted>{niche.patterns.your_edge}</Muted>
                        </ComparisonRow>
                    </Comparison>
                </Card>
            </Section>

            <Section icon={Lightbulb} title='Oportunidades'>
                {niche.opportunities.map((opportunity, index) => (
                    <Opportunity key={index} opportunity={opportunity} defaultOpen={index === 0} />
                ))}
            </Section>
        </>
    );
}

function Opportunity({ opportunity, defaultOpen }) {
    const [open, setOpen] = useState(defaultOpen);

    return (
        <Card style={{ padding: 0 }}>
            <OpportunityHead type='button' onClick={() => setOpen(!open)} aria-expanded={open}>
                <span>{opportunity.title}</span>
                <Chevron size={18} $open={open} />
            </OpportunityHead>
            {open && (
                <OpportunityBody>
                    <div>
                        <Label>O espaço</Label>
                        <Muted>{opportunity.opportunity}</Muted>
                    </div>
                    <div>
                        <Label>Por que você consegue</Label>
                        <Muted>{opportunity.why}</Muted>
                    </div>
                    <div>
                        <Label>Como fazer</Label>
                        <Muted>{opportunity.how}</Muted>
                    </div>
                </OpportunityBody>
            )}
        </Card>
    );
}

const Statement = styled.p`
    margin: 0;
    font-size: 15px;
    line-height: 1.5;
    color: ${colors.nightDark};
`;

const Reference = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 16px 18px;

    & + & {
        border-top: 1px solid ${colors.neutralDark};
    }
`;

const ReferenceHead = styled.div`
    display: flex;
    align-items: baseline;
    gap: 10px;
    flex-wrap: wrap;
`;

const ReferenceHandle = styled.a`
    font-size: 15px;
    font-weight: 600;
    color: ${panelAccent(colors.secondaryPurple)};
    text-decoration: none;

    &:hover {
        text-decoration: underline;
    }
`;

const ReferenceFollowers = styled.span`
    font-size: 12px;
    color: ${colors.nightLight};
`;

const Lesson = styled.p`
    margin: 0;
    font-size: 13px;
    line-height: 1.5;
    color: ${colors.nightDark};
`;

const Comparison = styled.div`
    display: flex;
    flex-direction: column;
`;

const ComparisonRow = styled.div`
    display: grid;
    grid-template-columns: 150px 1fr;
    gap: 12px;
    padding: 14px 18px;
    background: ${({ $highlighted }) => ($highlighted ? panelAccentAlpha(colors.secondaryPurple, '0D') : 'transparent')};

    & + & {
        border-top: 1px solid ${colors.neutralDark};
    }

    @media screen and (max-width: 600px) {
        grid-template-columns: 1fr;
        gap: 4px;
    }
`;

const ComparisonLabel = styled.span`
    font-size: 13px;
    font-weight: 600;
    color: ${colors.nightDark};
`;

const OpportunityHead = styled.button`
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 16px 18px;
    border: none;
    background: transparent;
    font: inherit;
    font-size: 15px;
    font-weight: 600;
    color: ${colors.nightDark};
    text-align: left;
    cursor: pointer;
`;

const Chevron = styled(ChevronDown)`
    flex-shrink: 0;
    color: ${colors.nightLight};
    transition: transform 0.2s ease;
    transform: rotate(${({ $open }) => ($open ? '180deg' : '0deg')});
`;

const OpportunityBody = styled.div`
    display: grid;
    gap: 14px;
    grid-template-columns: repeat(3, 1fr);
    padding: 0 18px 18px;

    @media screen and (max-width: 900px) {
        grid-template-columns: 1fr;
    }
`;
